import { supabase } from '@/integrations/supabase/client';

/**
 * Notification data-access layer.
 * Every notification read/write in the app goes through this module.
 */

export type NotificationType =
  | 'like'
  | 'comment'
  | 'reply'
  | 'follow'
  | 'repost'
  | 'message'
  | 'tag'
  | 'mention'
  | 'offer'
  | 'offer_application'
  | 'application'
  | 'application_accepted'
  | 'application_rejected'
  | 'club'
  | 'club_invitation'
  | 'invitation_accepted'
  | 'invitation_rejected'
  | 'event'
  | 'verification'
  | 'system';

export type NotificationEntityType =
  | 'post'
  | 'profile'
  | 'offer'
  | 'club'
  | 'team'
  | 'thread'
  | 'event'
  | 'comment';

export interface AppNotification {
  id: string;
  userId: string;
  actorId: string;
  type: NotificationType;
  title: string | null;
  message: string;
  targetId: string;
  targetType: NotificationEntityType;
  metadata: Record<string, unknown>;
  read: boolean;
  createdAt: string;
}

export interface NotificationPreferences {
  pushEnabled: boolean;
  emailEnabled: boolean;
  inAppEnabled: boolean;
}

export const NOTIFICATIONS_PAGE_SIZE = 20;

type Row = {
  id: string;
  user_id: string;
  actor_id: string | null;
  type: string;
  title: string | null;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  is_read: boolean;
  metadata: unknown;
  created_at: string;
};

export function mapNotification(row: Row): AppNotification {
  return {
    id: row.id,
    userId: row.user_id,
    actorId: row.actor_id ?? '',
    type: row.type as NotificationType,
    title: row.title,
    message: row.body ?? '',
    targetId: row.entity_id ?? '',
    targetType: (row.entity_type ?? 'profile') as NotificationEntityType,
    metadata: (row.metadata as Record<string, unknown>) ?? {},
    read: row.is_read,
    createdAt: row.created_at,
  };
}

/** Fetch one page of the signed-in user's notifications (newest first). */
export async function fetchNotificationsPage(
  userId: string,
  page: number,
  pageSize = NOTIFICATIONS_PAGE_SIZE,
): Promise<{ items: AppNotification[]; hasMore: boolean }> {
  const from = page * pageSize;
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .range(from, from + pageSize);
  if (error) throw error;
  const rows = (data ?? []) as Row[];
  return { items: rows.slice(0, pageSize).map(mapNotification), hasMore: rows.length > pageSize };
}

export async function fetchUnreadCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false);
  if (error) throw error;
  return count ?? 0;
}

export async function fetchNotificationById(id: string): Promise<AppNotification | null> {
  const { data, error } = await supabase.from('notifications').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data ? mapNotification(data as Row) : null;
}

export interface CreateNotificationInput {
  userId: string;
  actorId: string;
  type: NotificationType;
  targetId: string;
  targetType: NotificationEntityType;
  message: string;
  title?: string;
  metadata?: Record<string, unknown>;
}

/** Persist a notification. Returns null when the row was rejected (RLS / self-notify). */
export async function insertNotification(input: CreateNotificationInput): Promise<AppNotification | null> {
  const { data, error } = await supabase
    .from('notifications')
    .insert({
      user_id: input.userId,
      actor_id: input.actorId,
      type: input.type,
      title: input.title ?? null,
      body: input.message,
      entity_type: input.targetType,
      entity_id: input.targetId || null,
      metadata: (input.metadata ?? {}) as never,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return data ? mapNotification(data as Row) : null;
}

export async function markNotificationRead(id: string): Promise<void> {
  const { error } = await supabase.from('notifications').update({ is_read: true }).eq('id', id);
  if (error) throw error;
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false);
  if (error) throw error;
}

export async function deleteNotification(id: string): Promise<void> {
  const { error } = await supabase.from('notifications').delete().eq('id', id);
  if (error) throw error;
}

// ------------------------------------------------------------ preferences

export async function fetchPreferences(userId: string): Promise<NotificationPreferences> {
  const { data, error } = await supabase
    .from('notification_preferences')
    .select('push_enabled, email_enabled, in_app_enabled')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return {
    pushEnabled: data?.push_enabled ?? true,
    emailEnabled: data?.email_enabled ?? true,
    inAppEnabled: data?.in_app_enabled ?? true,
  };
}

export async function upsertPreferences(
  userId: string,
  prefs: Partial<NotificationPreferences>,
): Promise<void> {
  const { error } = await supabase.from('notification_preferences').upsert(
    {
      user_id: userId,
      ...(prefs.pushEnabled !== undefined ? { push_enabled: prefs.pushEnabled } : {}),
      ...(prefs.emailEnabled !== undefined ? { email_enabled: prefs.emailEnabled } : {}),
      ...(prefs.inAppEnabled !== undefined ? { in_app_enabled: prefs.inAppEnabled } : {}),
    },
    { onConflict: 'user_id' },
  );
  if (error) throw error;
}

/** Subscribe to realtime inserts/updates/deletes on the user's notifications. */
export function subscribeToNotifications(
  userId: string,
  handlers: {
    onInsert: (n: AppNotification) => void;
    onUpdate: (n: AppNotification) => void;
    onDelete: (id: string) => void;
  },
) {
  const channel = supabase
    .channel(`notifications:${userId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
      (payload) => handlers.onInsert(mapNotification(payload.new as Row)),
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
      (payload) => handlers.onUpdate(mapNotification(payload.new as Row)),
    )
    .on(
      'postgres_changes',
      { event: 'DELETE', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
      (payload) => handlers.onDelete((payload.old as { id: string }).id),
    )
    .subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
}
